import type { Api } from "../api";
import { empty, h, label, pill, sectionHead } from "../dom";
import { money, qty, shortDate, today } from "../format";
import { navigate } from "../nav";
import { viewMode, words } from "../orders";
import { byDay, calendarFlag, coverOrder, coverWords, windowEnd } from "../releases";
import type { Blanket, CalendarRelease, Page } from "../types";
import { modeNotice } from "./shared";

// Releases: what ships or delivers over the next weeks, by day (release_calendar), with what needs a look on each
// (past its date, a holiday or closure day, the supplier to confirm, the customer to approve); then each open PO's
// balances and how far its scheduled releases reach against the cover the customer wants (blanket_balances). Read
// only. The filters live in the address.

const WEEKS = [2, 4, 6];

export async function releases(api: Api, query: URLSearchParams): Promise<HTMLElement> {
  const customer = (query.get("customer") ?? "").slice(0, 100);
  const weeks = WEEKS.includes(Number(query.get("weeks"))) ? Number(query.get("weeks")) : 6;
  const from = /^\d{4}-\d{2}-\d{2}$/.test(query.get("from") ?? "") ? query.get("from")! : today();
  const asked = ["LIVE", "SIMULATION"].includes(query.get("mode") ?? "") ? query.get("mode")! : undefined;
  const scope = { ...(customer ? { customer } : {}), ...(asked ? { mode: asked } : {}) };
  const [cal, cover] = await Promise.all([
    api.tool<Page<CalendarRelease>>("release_calendar", { from, to: windowEnd(from, weeks), limit: 200, ...scope }),
    api.tool<Page<Blanket>>("blanket_balances", { limit: 200, ...scope }),
  ]);
  const view = viewMode(query, cal.mode);
  const now = today();
  const flagged = cal.rows.filter((r) => calendarFlag(r, now)).length;
  const short = cover.rows.filter((b) => b.coverLow).length;

  const customerInput = h("input", { id: "customer", type: "search", value: customer, placeholder: "LTMW, Creative…", maxlength: 100 });
  const fromInput = h("input", { id: "from", type: "date", value: from });
  const weeksSelect = h("select", { id: "weeks" }, WEEKS.map((w) => h("option", { value: String(w), selected: w === weeks }, `${w} weeks`)));
  const modeSelect = h("select", { id: "mode" }, (["LIVE", "SIMULATION"] as const).map((m) =>
    h("option", { value: m, selected: m === view.mode }, `${m === "LIVE" ? "Live orders" : "Simulation copies"}${m === cal.mode ? " (the platform's mode)" : ""}`)));
  const go = () => navigate(`/releases?${new URLSearchParams({
    ...(customerInput.value.trim() ? { customer: customerInput.value.trim() } : {}),
    ...(fromInput.value && fromInput.value !== now ? { from: fromInput.value } : {}), weeks: weeksSelect.value,
    ...(modeSelect.value !== cal.mode ? { mode: modeSelect.value } : {}) })}`);
  const form = h("form", { class: "filters", role: "search" },
    h("div", { class: "field" }, h("label", { for: "customer" }, "Customer"), customerInput),
    h("div", { class: "field" }, h("label", { for: "from" }, "From"), fromInput),
    h("div", { class: "field" }, h("label", { for: "weeks" }, "For"), weeksSelect),
    h("div", { class: "field" }, h("label", { for: "mode" }, "Showing"), modeSelect),
    h("button", { type: "submit", class: "button" }, "Show"));
  form.addEventListener("submit", (e) => { e.preventDefault(); go(); });
  for (const el of [weeksSelect, modeSelect, fromInput]) el.addEventListener("change", go);

  const days = byDay(cal.rows);

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Releases"),
      h("p", { class: "lede" },
        `${cal.rows.length}${cal.next_cursor ? "+" : ""} ${cal.rows.length === 1 ? "release" : "releases"} from ${shortDate(from)} ` +
        `to ${shortDate(windowEnd(from, weeks))}${flagged ? `, ${flagged} to look at` : ""}; ` +
        `${cover.rows.length} open ${cover.rows.length === 1 ? "PO" : "POs"}${short ? `, ${short} short of cover` : ""}.`)),
    form,
    view.other ? modeNotice(view.mode, cal.mode) : null,

    h("section", { class: "stack", "aria-labelledby": "calendar" },
      sectionHead("calendar", "By day", "Delivery day, or ship day for a pickup"),
      days.length
        ? h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
            h("thead", null, h("tr", null,
              h("th", { scope: "col" }, "Day"), h("th", { scope: "col" }, "Customer"), h("th", { scope: "col" }, "PO"),
              h("th", { scope: "col" }, "Release"), h("th", { scope: "col" }, "Supplier"), h("th", { scope: "col", class: "r" }, "EA"),
              h("th", { scope: "col", class: "r" }, "Sell"), h("th", { scope: "col" }, "Status"), h("th", { scope: "col" }, "Needs a look"))),
            h("tbody", null, days.flatMap((d) => d.rows.map((r, i) => {
              const flag = calendarFlag(r, now);
              return h("tr", null,
                h("td", { class: "num strong" }, i === 0 ? shortDate(d.day) : ""),
                h("td", null, r.customer?.name ?? ""),
                h("td", { class: "num" }, h("a", { href: `/order/${r.customerPoId}${view.param}` }, r.poNumber)),
                h("td", { class: "num" }, String(r.releaseNo), r.dateIs.includes("ship") ? h("div", { class: "caption" }, "pickup / ship") : null),
                h("td", null, r.supplier?.name ?? ""),
                h("td", { class: "num r" }, qty(r.qtyEa, "")),
                h("td", { class: "num r" }, money(r.sellValue)),
                h("td", null, words(r.status)),
                h("td", null, flag ? pill(flag.text.toLowerCase(), flag.tone) : h("span", { class: "muted" }, "—")));
            })))))
        : empty("Nothing scheduled", "No releases in these weeks. Try a longer window, or another customer."),
      cal.next_cursor ? h("p", { class: "caption" }, "More than 200 releases: narrow it by customer or a shorter window.") : null),

    h("section", { class: "stack", "aria-labelledby": "cover" },
      sectionHead("cover", "Cover on open POs", "What's left on each PO, and how far its releases reach"),
      cover.rows.length
        ? coverOrder(cover.rows).map((b) => {
            const c = coverWords(b);
            return h("div", { class: "panel decision" },
              h("div", { class: "row" },
                h("div", null,
                  h("h3", { class: "subheading" }, b.customer?.name ?? "", " · PO ",
                    h("a", { class: "num", href: `/order/${b.customerPoId}${view.param}` }, b.poNumber)),
                  b.lastScheduledDelivery ? h("p", { class: "caption" }, `Last release scheduled ${shortDate(b.lastScheduledDelivery)}`) : null),
                pill(c.text.toLowerCase(), c.tone)),
              h("div", { class: "scroll" }, h("table", { class: "tbl" },
                h("thead", null, h("tr", null,
                  h("th", { scope: "col" }, "SKU"), h("th", { scope: "col", class: "r" }, "Ordered"), h("th", { scope: "col", class: "r" }, "Released"),
                  h("th", { scope: "col", class: "r" }, "Shipped"), h("th", { scope: "col", class: "r" }, "Scheduled, not shipped"),
                  h("th", { scope: "col", class: "r" }, "Not yet released"))),
                h("tbody", null, b.items.map((it) => h("tr", null,
                  h("td", { class: "num" }, it.sku), h("td", { class: "num r" }, qty(it.orderedEa, "")),
                  h("td", { class: "num r" }, qty(it.releasedEa, "")), h("td", { class: "num r" }, qty(it.shippedEa, "")),
                  h("td", { class: "num r" }, qty(it.scheduledNotShippedEa, "")),
                  h("td", { class: "num r strong" }, qty(it.unreleasedEa, ""))))))));
          })
        : empty("No open POs", "Nothing open for this customer.")),
    label(view.mode === "SIMULATION" ? "Simulation copies" : "Live orders"));
}
